import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Car, MapPin, Clock, Camera, Trash2, Bluetooth, Search, X, Target, RefreshCw, Navigation, Image } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';

// Apple MapKit JS Tokens - Multiple origins supported
const MAPKIT_TOKENS: Record<string, string> = {
    'lifeshare-1465d.web.app': 'eyJraWQiOiJCOEhBTjlUTTg5IiwidHlwIjoiSldUIiwiYWxnIjoiRVMyNTYifQ.eyJpc3MiOiI5NDRZQUdGNkpRIiwiaWF0IjoxNzY5NzUzMzgwLCJvcmlnaW4iOiJsaWZlc2hhcmUtMTQ2NWQud2ViLmFwcCJ9.71FYe9c-PTgED4ghBe6OzbEjdcB4aznqLGZDk5U_CaCAiM8vhXv73j9ij66xYgsiAvljyEfqGjyvmsLc2VyKbA',
    'admin.lifeshare.app': 'eyJraWQiOiJQOFNUMzlOTkROIiwidHlwIjoiSldUIiwiYWxnIjoiRVMyNTYifQ.eyJpc3MiOiI5NDRZQUdGNkpRIiwiaWF0IjoxNzY5NzUzMTc4LCJvcmlnaW4iOiJhZG1pbi5saWZlc2hhcmUuYXBwIn0.1NHZ-er4bqWF3ja5TB-q60KiRr5zYz7-E2gF7S0ifpMSQUMBaHBNPVgjYeOXsWQHrwQW5BKivmvwMn0lDs3vaQ'
};
const getMapKitToken = (): string => MAPKIT_TOKENS[window.location.hostname] || MAPKIT_TOKENS['admin.lifeshare.app'];

declare global {
  interface Window { mapkit: any; }
}

const StatCard: React.FC<{ icon: React.ReactNode; value: number | string; label: string; color: string }> = ({ icon, value, label, color }) => {
  const colors: Record<string, string> = {
    green: 'from-green-50 to-green-100 border-green-200 text-green-700',
    blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
    purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700',
    orange: 'from-orange-50 to-orange-100 border-orange-200 text-orange-700',
    red: 'from-red-50 to-red-100 border-red-200 text-red-700',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-2 text-center`}>
      <div className="flex justify-center mb-1 opacity-60 [&>svg]:w-3.5 [&>svg]:h-3.5">{icon}</div>
      <p className="text-lg font-bold">{value}</p>
      <p className="text-[10px] opacity-60 uppercase tracking-wide">{label}</p>
    </div>
  );
};

interface ParkingItem {
  id: number;
  user_id: number;
  username: string | null;
  user_email: string | null;
  latitude: number;
  longitude: number;
  address: string | null;
  notes: string | null;
  vehicle_name: string | null;
  photo_url: string | null;
  is_auto_detected: boolean;
  parked_at: string | null;
}

interface ParkingStats {
  total: number;
  auto_detected: number;
  manual: number;
  with_photo: number;
  with_notes: number;
  recent_24h: number;
}

type FilterType = 'all' | 'auto' | 'photo';

const ParkingManagement: React.FC = () => {
  const { token } = useAuth();
  const [parking, setParking] = useState<ParkingItem[]>([]);
  const [stats, setStats] = useState<ParkingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [selectedParking, setSelectedParking] = useState<ParkingItem | null>(null);
  const [mapStatus, setMapStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const annotationsRef = useRef<any[]>([]);

  const filtered = useMemo(() => {
    let result = parking;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.username?.toLowerCase().includes(q) ||
        p.address?.toLowerCase().includes(q) ||
        p.vehicle_name?.toLowerCase().includes(q)
      );
    }
    switch (activeFilter) {
      case 'auto': result = result.filter(p => p.is_auto_detected); break;
      case 'photo': result = result.filter(p => p.photo_url); break;
    }
    return result;
  }, [parking, search, activeFilter]);

  // Initialize MapKit
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 100;
    let cancelled = false;

    const initMapKit = () => {
      if (cancelled) return;
      if (!window.mapkit) {
        retryCount++;
        if (retryCount < maxRetries) setTimeout(initMapKit, 200);
        else setMapStatus('error');
        return;
      }
      if (!mapContainerRef.current) {
        retryCount++;
        if (retryCount < maxRetries) setTimeout(initMapKit, 200);
        return;
      }

      try {
        // Only init if not already initialized
        if (!window.mapkit._initialized) {
          window.mapkit.init({
            authorizationCallback: (done: (t: string) => void) => done(getMapKitToken()),
            language: 'tr'
          });
          window.mapkit._initialized = true;
        }

        const map = new window.mapkit.Map(mapContainerRef.current, {
          center: new window.mapkit.Coordinate(39.9334, 32.8597),
          cameraDistance: 5000000,
          mapType: window.mapkit.Map.MapTypes.Standard,
          showsCompass: window.mapkit.FeatureVisibility.Visible,
          showsScale: window.mapkit.FeatureVisibility.Visible,
          showsMapTypeControl: true,
          showsZoomControl: true
        });

        mapRef.current = map;
        setMapStatus('ready');
      } catch {
        setMapStatus('error');
      }
    };

    setTimeout(initMapKit, 300);
    return () => {
      cancelled = true;
      if (mapRef.current) {
        try { mapRef.current.destroy(); } catch {}
        mapRef.current = null;
      }
    };
  }, []);

  // Update annotations
  useEffect(() => {
    if (mapStatus !== 'ready' || !mapRef.current || !window.mapkit) return;

    try {
      if (annotationsRef.current.length > 0) {
        mapRef.current.removeAnnotations(annotationsRef.current);
      }

      const annotations = filtered.map(p => {
        const coord = new window.mapkit.Coordinate(p.latitude, p.longitude);
        const color = p.is_auto_detected ? '#3b82f6' : '#8b5cf6';
        return new window.mapkit.MarkerAnnotation(coord, {
          color,
          title: p.vehicle_name || p.username || 'Park',
          subtitle: p.address || '',
          glyphText: '🚗'
        });
      });

      mapRef.current.addAnnotations(annotations);
      annotationsRef.current = annotations;

      if (annotations.length > 0) {
        mapRef.current.showItems(annotations, {
          animate: true,
          padding: new window.mapkit.Padding(50, 50, 50, 50)
        });
      }
    } catch {}
  }, [filtered, mapStatus]);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const [parkingRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/admin/parking?limit=200`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/admin/parking/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (parkingRes.ok) setParking((await parkingRes.json()).items || []);
      if (statsRes.ok) setStats(await statsRes.json());
    } catch {
      setError('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, [token]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bu park konumunu silmek istediğinize emin misiniz?')) return;
    try {
      const res = await fetch(`${API_URL}/admin/parking/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setParking(parking.filter(p => p.id !== id));
        setSelectedParking(null);
      }
    } catch {}
  };

  const focusOnParking = (p: ParkingItem) => {
    setSelectedParking(p);
    if (mapRef.current && window.mapkit) {
      mapRef.current.region = new window.mapkit.CoordinateRegion(
        new window.mapkit.Coordinate(p.latitude, p.longitude),
        new window.mapkit.CoordinateSpan(0.005, 0.005)
      );
    }
  };

  const getTimeDiff = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'Az önce';
    if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} sa`;
    return `${Math.floor(diff / 86400)} gün`;
  };

  const filters: { key: FilterType; label: string; icon: React.ReactNode; count: number }[] = [
    { key: 'all', label: 'Tümü', icon: <Car className="w-3.5 h-3.5" />, count: parking.length },
    { key: 'auto', label: 'Otomatik', icon: <Bluetooth className="w-3.5 h-3.5" />, count: parking.filter(p => p.is_auto_detected).length },
    { key: 'photo', label: 'Fotoğraflı', icon: <Camera className="w-3.5 h-3.5" />, count: parking.filter(p => p.photo_url).length },
  ];

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Park Yönetimi</h1>
            <p className="text-sm text-gray-500 mt-1">{parking.length} park kaydı</p>
          </div>
          <button
            onClick={fetchData}
            disabled={refreshing}
            className="p-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && <div className="bg-red-50 text-red-600 p-4 rounded-2xl">{error}</div>}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            <StatCard icon={<Car />} value={stats.total} label="Toplam" color="purple" />
            <StatCard icon={<Bluetooth />} value={stats.auto_detected} label="Otomatik" color="blue" />
            <StatCard icon={<Camera />} value={stats.with_photo} label="Fotoğraf" color="orange" />
            <StatCard icon={<Navigation />} value={stats.with_notes} label="Notlu" color="green" />
            <StatCard icon={<Clock />} value={stats.recent_24h} label="24 Saat" color="red" />
          </div>
        )}

        {/* Search & Filters */}
        <div className="bg-white rounded-2xl p-4">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Araç, kullanıcı veya adres ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-10 py-3 bg-gray-50 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {filters.map(f => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${activeFilter === f.key ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {f.icon}
                {f.label}
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${activeFilter === f.key ? 'bg-purple-700' : 'bg-gray-200'}`}>{f.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Map */}
        <div
          ref={mapContainerRef}
          className="rounded-2xl overflow-hidden shadow-lg relative"
          style={{ height: '280px', background: '#f3f4f6' }}
        >
          {mapStatus === 'loading' && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-3 border-purple-500 border-t-transparent rounded-full mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Harita yükleniyor...</p>
              </div>
            </div>
          )}
          {mapStatus === 'error' && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <p className="text-red-500 text-sm">Harita yüklenemedi</p>
            </div>
          )}
          <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full shadow-sm text-xs font-medium text-gray-700">
            🚗 {filtered.length} park konumu
          </div>
        </div>

        {/* Legend */}
        <div className="flex justify-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" />Otomatik</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-purple-500" />Manuel</span>
        </div>

        {/* Parking List */}
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <Car className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">{search ? 'Arama sonucu bulunamadı' : 'Park kaydı bulunamadı'}</p>
            </div>
          ) : (
            filtered.slice(0, 50).map(p => (
              <div
                key={p.id}
                onClick={() => focusOnParking(p)}
                className={`bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-200 cursor-pointer transition-all ${selectedParking?.id === p.id ? 'ring-2 ring-purple-500' : ''}`}
              >
                <div className="flex items-center gap-3">
                  {/* Photo or Icon */}
                  <div className="relative flex-shrink-0">
                    {p.photo_url ? (
                      <img src={`https://api.lifeshare.app${p.photo_url}`} className="w-14 h-14 rounded-xl object-cover" alt="" />
                    ) : (
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${p.is_auto_detected ? 'bg-blue-100' : 'bg-purple-100'}`}>
                        <Car className={`w-6 h-6 ${p.is_auto_detected ? 'text-blue-600' : 'text-purple-600'}`} />
                      </div>
                    )}
                    {p.is_auto_detected && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Bluetooth className="w-3 h-3 text-white" />
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900 truncate">
                        {p.vehicle_name || p.username || 'Araç'}
                      </p>
                      {p.photo_url && <Image className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" />
                      {p.address || `${p.latitude.toFixed(4)}, ${p.longitude.toFixed(4)}`}
                    </p>
                    {p.notes && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">📝 {p.notes}</p>
                    )}
                  </div>

                  {/* Right */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-500">{getTimeDiff(p.parked_at)}</p>
                    <p className="text-xs text-gray-400 mt-1">@{p.username || '-'}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); focusOnParking(p); }}
                      className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      <Target className="w-4 h-4 text-gray-400" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}
                      className="p-2 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
          {filtered.length > 50 && (
            <p className="text-center text-xs text-gray-400 py-2">
              +{filtered.length - 50} daha fazla kayıt
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParkingManagement;
